import { Link } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Card, Logo, MonoLabel } from '../components/primitives/index.js';
import { ContestRow } from '../components/composites/index.js';

export function Dashboard() {
  const me = useRequireAuth();
  const contests = useQuery({
    queryKey: ['me', 'contests'],
    queryFn: api.myContests,
    enabled: !!me.data,
  });

  if (me.isLoading || (me.data && contests.isLoading)) {
    return (
      <Shell>
        <div className="text-muted text-sm py-12 text-center">Se încarcă…</div>
      </Shell>
    );
  }

  if (!me.data) return null;

  const { user, stats } = me.data;
  const list = contests.data?.contests ?? [];
  const empty = list.length === 0;

  return (
    <Shell>
      {/* Hero card with running totals */}
      <Card className="mb-6 bg-fg text-bg border-fg overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 sm:gap-6">
          <div>
            <MonoLabel color="oklch(0.72 0.18 145)">Bun venit înapoi</MonoLabel>
            <div className="mt-2 text-2xl sm:text-3xl font-medium break-words">{user.name ?? user.email}</div>
          </div>
          <div className="sm:text-right">
            <MonoLabel color="oklch(0.72 0.18 145)">Economisit prin Anulează</MonoLabel>
            <div className="mt-1 text-4xl sm:text-5xl lg:text-display-3 font-medium text-accent">
              {user.total_saved_lei.toLocaleString('ro-RO')} lei
            </div>
          </div>
        </div>
      </Card>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <StatCard label="Pe rol" value={stats.pe_rol} />
        <StatCard label="Câștigate" value={stats.castigate} accent />
        <StatCard label="Pierdute" value={stats.pierdute} />
        <StatCard label="Total economisit" value={`${stats.total_economisit.toLocaleString('ro-RO')} lei`} />
      </div>

      {/* List or empty state */}
      {empty ? (
        <EmptyState />
      ) : (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
            <h2 className="text-xl font-medium">Contestațiile tale</h2>
            <Link href="/contest/new">
              <Button size="md" pill>+ Contestație nouă</Button>
            </Link>
          </div>
          <div className="grid gap-3">
            {list.map((c) => (
              <ContestRow key={c.id} contest={c} />
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <Card className="p-5">
      <MonoLabel>{label}</MonoLabel>
      <div
        className={[
          'mt-3 text-2xl font-medium tabular-nums',
          accent ? 'text-accent-dark' : 'text-fg',
        ].join(' ')}
      >
        {value}
      </div>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card className="text-center py-12 sm:py-16 px-4 sm:px-8">
      <div className="text-6xl mb-4">📄</div>
      <h2 className="text-2xl font-medium mb-3">Niciun dosar deocamdată</h2>
      <p className="text-muted max-w-md mx-auto leading-relaxed mb-6">
        Începe prin a fotografia procesul-verbal. AI-ul extrage datele și verifică ce articole pot
        fi atacate. În ~3 minute ai plângerea gata.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link href="/contest/new">
          <Button size="lg" pill>Începe prima contestație</Button>
        </Link>
        <Link href="/privacy">
          <Button size="lg" pill variant="ghost" icon={false}>
            Cum protejăm datele
          </Button>
        </Link>
      </div>
    </Card>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const logout = useMutation({
    mutationFn: api.logout,
    onSettled: () => {
      qc.clear();
      window.location.href = '/';
    },
  });

  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line bg-bg/85 backdrop-blur-xl sticky top-0 z-10 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-3 sm:gap-4 text-sm">
          <Link href="/dashboard" className="text-fg font-medium">Dosare</Link>
          <Link href="/privacy" className="text-muted hidden sm:inline">Confidențialitate</Link>
          <button
            onClick={() => logout.mutate()}
            className="text-muted hover:text-fg"
            disabled={logout.isPending}
          >
            Ieșire
          </button>
        </div>
      </nav>
      <main className="px-4 md:px-8 py-6 md:py-10 max-w-5xl mx-auto">{children}</main>
    </div>
  );
}
